import type { ConversaDetalhe } from '@mymaxx/shared';
import { Botao, Modal } from '@mymaxx/ui';
import { useQuery } from '@tanstack/react-query';
import { MessagesSquare } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { internoApi } from '../lib/interno-api';

/**
 * Leva o assunto deste atendimento para uma conversa da equipe.
 *
 * É o botão que tira do Telegram a última razão de existir. Hoje, para perguntar algo
 * sobre um protocolo, a pessoa copia o número na mão, troca de aplicativo e procura o
 * grupo certo — e a resposta volta num lugar onde a empresa não enxerga.
 *
 * **Não é sussurrar.** Sussurro é aqui dentro, com quem já está neste atendimento; isto é
 * levar a dúvida para fora dele: o grupo de Rotas, o técnico, quem não tem nada a ver com
 * este protocolo mas sabe a resposta.
 *
 * E não manda nada sozinho: abre a conversa com o começo da frase pronto e deixa a pessoa
 * escrever. Uma mensagem automática dizendo só "sobre o protocolo X" é ruído para quem a
 * recebe — e quem a recebe é gente trabalhando.
 */
export function LevarAsConversas({ conversa }: { conversa: ConversaDetalhe }) {
  const [escolhendo, setEscolhendo] = useState(false);
  const navegar = useNavigate();
  const canais = useQuery({
    queryKey: ['interno-canais'],
    queryFn: internoApi.canais,
    enabled: escolhendo,
  });

  const sobre = `Sobre o atendimento ${conversa.protocolo}${
    conversa.cliente ? ` (${conversa.cliente})` : ''
  }: `;

  return (
    <>
      <Botao
        variante="secundario"
        onClick={() => setEscolhendo(true)}
        title="Perguntar à equipe, numa conversa interna, sobre este atendimento"
      >
        <MessagesSquare size={15} /> Levar às Conversas
      </Botao>

      {escolhendo && (
        <Modal
          aberto
          titulo="Levar às Conversas"
          descricao={`A conversa abre com "${conversa.protocolo}" já escrito. O cliente não vê nada disso.`}
          onFechar={() => setEscolhendo(false)}
        >
          <ul className="max-h-[50vh] space-y-1 overflow-y-auto">
            {(canais.data ?? []).map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() =>
                    navegar(`/interno?canal=${c.id}&sobre=${encodeURIComponent(sobre)}`)
                  }
                  className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-left text-sm hover:bg-neutral-50 dark:border-neutral-700 dark:hover:bg-neutral-900"
                >
                  {c.nome}
                </button>
              </li>
            ))}
            {canais.isPending && <li className="p-3 text-sm text-neutral-500">Carregando…</li>}
            {canais.isSuccess && canais.data.length === 0 && (
              <li className="py-6 text-center text-sm text-neutral-500">
                Você ainda não tem conversas internas. Comece uma na aba Conversas.
              </li>
            )}
          </ul>
        </Modal>
      )}
    </>
  );
}
